
import { Component, Prop, Vue } from 'vue-property-decorator';
import { DnsCheckerRepositoryInterface, DnsCheckResult } from '@/services/DnsChecker';
import DnsNsMapPanel from '@/components/website/DnsNSMapPanel.vue';

const HOVER_OPEN_MS = 1000;
const CARD_WIDTH = 560;
const CARD_MAX_HEIGHT_VH = 0.55;

@Component({
  components: { DnsNsMapPanel },
  inject: ['dnsCheckerRepository'],
})
export default class DomainDnsHover extends Vue {
  @Prop({ type: String, required: true })
  public domain!: string;

  @Prop({ type: [Number, String], required: true })
  public websiteId!: number | string;

  private dnsCheckerRepository!: DnsCheckerRepositoryInterface;

  public menuOpen = false;
  public loading = false;
  public error: string | null = null;
  public result: DnsCheckResult | null = null;
  public cardStyle: Record<string, string> = {};

  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  public get providerColor(): string {
    switch (this.result && this.result.provider) {
      case 'wpx':
        return 'success';
      case 'cloudflare':
        return 'orange';
      case 'gcp':
        return 'blue';
      case 'sucuri':
        return 'purple';
      default:
        return 'grey';
    }
  }

  public onMouseEnter(): void {
    this.clearCloseTimer();
    if (this.menuOpen) {
      return;
    }
    this.clearOpenTimer();
    this.openTimer = setTimeout(() => {
      this.positionCard();
      this.menuOpen = true;
      this.loadDns();
    }, HOVER_OPEN_MS);
  }

  public onMouseLeave(): void {
    this.clearOpenTimer();
    this.clearCloseTimer();
    this.closeTimer = setTimeout(() => {
      this.menuOpen = false;
    }, 200);
  }

  public onCardEnter(): void {
    this.clearCloseTimer();
  }

  public beforeDestroy(): void {
    this.clearOpenTimer();
    this.clearCloseTimer();
  }

  private positionCard(): void {
    const el = this.$refs.anchor as HTMLElement | undefined;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const width = Math.min(CARD_WIDTH, window.innerWidth - 16);
    const maxHeight = Math.round(window.innerHeight * CARD_MAX_HEIGHT_VH);
    let left = Math.round(rect.left);
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }

    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const preferAbove = spaceBelow < Math.min(280, maxHeight) && spaceAbove > spaceBelow;

    if (preferAbove) {
      this.cardStyle = {
        top: 'auto',
        bottom: `${Math.round(window.innerHeight - rect.top + 6)}px`,
        left: `${left}px`,
        width: `${width}px`,
        maxHeight: `${Math.min(maxHeight, Math.max(160, spaceAbove))}px`,
      };
      return;
    }

    this.cardStyle = {
      top: `${Math.round(rect.bottom + 6)}px`,
      bottom: 'auto',
      left: `${left}px`,
      width: `${width}px`,
      maxHeight: `${Math.min(maxHeight, Math.max(160, spaceBelow))}px`,
    };
  }

  private clearOpenTimer(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }

  private clearCloseTimer(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private async loadDns(): Promise<void> {
    if (this.result || this.loading) {
      return;
    }

    this.loading = true;
    this.error = null;
    // eslint-disable-next-line no-console
    console.info('[dns-checker] hover lookup start', this.domain);

    try {
      this.result = await this.dnsCheckerRepository.checkDomain(this.domain);
      // eslint-disable-next-line no-console
      console.info('[dns-checker] hover lookup ok', this.domain, this.result.provider);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[dns-checker] hover lookup failed', this.domain, err);
      this.error = "Can't check DNS for this domain (timeout or checker unavailable).";
    } finally {
      this.loading = false;
    }
  }
}
